import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Canvas } from '@react-three/fiber';
import { Vector3Tuple } from 'three';
import CameraControl from '~/components/Canvas/CameraControl';
import CanvasLights from '~/components/Canvas/CanvasLights';
import { AvatarInfo } from '~/types';
import { CanvasOptions } from '~/constants/Canvas';
import { useChatAvatarStore } from '~/store';
import useChatPageLoadingStatus from '~/store/useChatPageLoadingStatusStore';
import useToast from '~/store/useToast';
import Background from './Background';
import Character from './Character/Character';

interface ChatAvatarCanvasProps {
  visible?: boolean;
  avatars?: AvatarInfo[] | null;
  /**
   * 아바타 그룹 모드 여부
   */
  group?: boolean;
  /**
   * 아바타 오버 뷰 모드 여부 - true일 경우 화면에서 멀어짐
   */
  isOverviewMode?: boolean;
  /**
   * 상호작용 비활성화 여부
   */
  disableInteraction?: boolean;
  /**
   * 3D 배경 이미지 주소
   */
  bgTextureUrl?: string;
  /**
   * 2D 배경 이미지 주소
   */
  bgImageUrl?: string;
}

export default function ChatAvatarCanvas({
  isOverviewMode = false,
  visible = true,
  avatars = null,
  group = false,
  bgTextureUrl,
  bgImageUrl,
  disableInteraction = false,
}: ChatAvatarCanvasProps) {
  const fov = useMemo(
    () => (group ? CanvasOptions.groupChatFov : CanvasOptions.fov),
    [group],
  );
  const { showToast } = useToast();

  const { setChatPageLoadingEnd, isChatPageLoading } =
    useChatPageLoadingStatus();

  const cameraStates = useMemo(() => {
    return {
      position: [0, 10, isOverviewMode ? 22 : 23] as Vector3Tuple,
      target: [0, isOverviewMode ? 8 : 11, 0] as Vector3Tuple,
    };
  }, [isOverviewMode]);

  const resetAvatarState = useChatAvatarStore(
    (state) => state.resetAvatarState,
  );

  const [isAvatarLoadOnError, setIsAvatarLoadOnError] = useState(false);

  useEffect(() => {
    return () => {
      resetAvatarState();
    };
  }, [resetAvatarState]);

  // 아바타 데이터가 존재하지 않을 경우 에러 처리
  useEffect(() => {
    // 아바타가 아직 로딩중 => return
    if (avatars === null) return;

    // 아바타 데이터가 존재함 => return
    if (avatars?.length > 0) return;

    // 채팅방 데이터에 아바타 데이터가 없을 경우 -> error toast 띄움
    // 채팅방 진입 로딩 레이아웃 깜빡임을 방지하기 위해 1초 대기 후 로딩 레이아웃 제거
    setTimeout(() => {
      if (isChatPageLoading) setChatPageLoadingEnd();
      showToast(
        'error',
        'Unable to render Avatar. This chat has no avatar data.',
      );
      setIsAvatarLoadOnError(true);
    }, 1000);
  }, [avatars]);

  useEffect(() => {
    if (isAvatarLoadOnError) throw new Error(`Avatar 파일 로드 실패`);
  }, [isAvatarLoadOnError]);

  return (
    <Canvas
      css={
        !bgTextureUrl &&
        css`
          background: ${bgImageUrl
            ? `url(${bgImageUrl})`
            : `radial-gradient(
            50% 50% at 50% 50%,
            #3a3a3a 0%,
            #09090a 100%
          )`};
          background-repeat: no-repeat;
          background-position: center;
          background-size: cover;
        `
      }
    >
      <CanvasLights />
      {bgTextureUrl && <Background imageUrl={bgTextureUrl} />}
      <CameraControl
        position={cameraStates.position}
        target={cameraStates.target}
        animationDuration={1}
        minPolarAngle={Math.PI / 2.2}
        maxPolarAngle={Math.PI / 2.2}
        fov={fov}
        disableInteraction={disableInteraction}
      />

      {avatars &&
        avatars.map((avatar, index) => {
          const spacing = 4.3;
          const position = group
            ? ([
                -2.2 + index * spacing,
                CanvasOptions.character_position[1],
                CanvasOptions.character_position[2],
              ] as Vector3Tuple)
            : (CanvasOptions.character_position as Vector3Tuple);

          return (
            <Character
              key={avatar.id}
              avatar={avatar}
              visible={visible}
              position={position}
            />
          );
        })}
    </Canvas>
  );
}
